import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, User, Clock, FileText, CreditCard } from 'lucide-react';

const PersonalLoan = () => {
  const features = [
    {
      icon: <User className="h-6 w-6" />,
      title: "No Collateral Required",
      description: "Unsecured loan with no need for security or guarantor"
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "Quick Processing",
      description: "Get approval within 24 hours with minimal documentation"
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: "Minimal Documentation",
      description: "Simple paperwork with easy verification process"
    },
    {
      icon: <CreditCard className="h-6 w-6" />,
      title: "Flexible Repayment",
      description: "Choose tenure from 12 to 60 months as per your convenience"
    }
  ];

  const loanDetails = [
    {
      category: "Interest Rate",
      details: "Starting from 16% p.a.*",
      note: "Rates vary based on credit profile and income"
    },
    {
      category: "Processing Fee",
      details: "Up to 2% of loan amount",
      note: "Subject to minimum fee applicable"
    },
    {
      category: "Prepayment Charges",
      details: "Nil for loans above ₹1 lakh",
      note: "No prepayment penalty after 6 months"
    },
    {
      category: "Late Payment Fee",
      details: "₹500 + GST per overdue EMI",
      note: "Waived off for first-time delays"
    }
  ];

  const eligibility = [
    "Age: 21 to 58 years",
    "Employment: Salaried or Self-employed",
    "Monthly Income: Minimum ₹25,000",
    "Work Experience: Minimum 2 years",
    "CIBIL Score: 650 and above",
    "Residence: Indian Citizen/NRI"
  ];

  const documents = [
    "Identity Proof (Aadhaar/PAN/Passport)",
    "Address Proof (Utility Bills/Rental Agreement)",
    "Income Proof (Salary Slips/ITR)",
    "Bank Statements (Last 3 months)",
    "Employment Proof (Offer Letter/ID Card)",
    "Recent Photographs"
  ];

  const usages = [
    "Wedding Expenses",
    "Medical Emergency",
    "Home Renovation",
    "Education Expenses",
    "Travel & Vacation",
    "Debt Consolidation",
    "Business Investment",
    "Consumer Durables"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                  Personal Loan for
                  <span className="block" style={{ color: '#340C7F' }}>
                    Every Need
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Get instant personal loans with competitive interest rates 
                  starting from 16% p.a. No collateral, quick approval, minimal documentation.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>Attractive</p>
                  <p className="text-sm text-gray-600">Loan Amount</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>16%*</p>
                  <p className="text-sm text-gray-600">Interest Rate</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>12-60</p>
                  <p className="text-sm text-gray-600">Months Tenure</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>24 Hours</p>
                  <p className="text-sm text-gray-600">Quick Approval</p>
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white rounded-lg hover:opacity-90 transition-opacity shadow-lg"
                style={{ backgroundColor: '#340C7F' }}
              >
                Apply Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
                alt="Personal Loan" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Why Choose Our <span style={{ color: '#340C7F' }}>Personal Loan?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center space-y-4 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full text-white" 
                     style={{ backgroundColor: '#340C7F' }}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Loan Details & Terms Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Loan <span style={{ color: '#340C7F' }}>Terms & Conditions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent pricing with no hidden charges. Know exactly what you're paying for.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {loanDetails.map((detail, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">{detail.category}</h3>
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>{detail.details}</p>
                  <p className="text-sm text-gray-600">{detail.note}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-white rounded-2xl p-8 shadow-sm">
            <div className="text-center space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">Application Process</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center text-white font-bold text-lg"
                       style={{ backgroundColor: '#340C7F' }}>1</div>
                  <h4 className="font-semibold text-gray-900 mb-2">Apply Online</h4>
                  <p className="text-sm text-gray-600">Fill out our simple application form</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center text-white font-bold text-lg"
                       style={{ backgroundColor: '#340C7F' }}>2</div>
                  <h4 className="font-semibold text-gray-900 mb-2">Document Upload</h4>
                  <p className="text-sm text-gray-600">Submit required documents digitally</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center text-white font-bold text-lg"
                       style={{ backgroundColor: '#340C7F' }}>3</div>
                  <h4 className="font-semibold text-gray-900 mb-2">Verification</h4>
                  <p className="text-sm text-gray-600">Quick verification and credit check</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center text-white font-bold text-lg"
                       style={{ backgroundColor: '#340C7F' }}>4</div>
                  <h4 className="font-semibold text-gray-900 mb-2">Disbursal</h4>
                  <p className="text-sm text-gray-600">Loan amount credited to your account</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Loan Usage Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Use Personal Loan <span style={{ color: '#340C7F' }}>For Anything</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our personal loans come with no end-use restrictions. Use the funds for any legitimate purpose.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {usages.map((usage, index) => (
              <div key={index} className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle className="h-8 w-8 mx-auto mb-3" style={{ color: '#340C7F' }} />
                <p className="font-medium text-gray-900">{usage}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility & Documents Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Eligibility Criteria */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Eligibility <span style={{ color: '#340C7F' }}>Criteria</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Check if you meet our simple eligibility requirements for personal loan approval.
                </p>
              </div>

              <ul className="space-y-4">
                {eligibility.map((criterion, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{criterion}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Required Documents */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Required <span style={{ color: '#340C7F' }}>Documents</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Keep these documents ready for a smooth and quick loan application process.
                </p>
              </div>

              <ul className="space-y-4">
                {documents.map((document, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{document}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Interest Rate Comparison Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Compare Our <span style={{ color: '#340C7F' }}>Interest Rates</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent pricing with competitive rates across different customer segments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                category: "Premium Customers",
                rate: "16.0% - 18.0%",
                features: ["CIBIL Score 750+", "High Income", "Stable Employment", "Existing Relationship"],
                highlight: true
              },
              {
                category: "Standard Customers",
                rate: "18.0% - 22.0%",
                features: ["CIBIL Score 650-750", "Regular Income", "Employment Stability", "Good Credit History"],
                highlight: false
              },
              {
                category: "New Customers",
                rate: "22.0% - 26.0%",
                features: ["CIBIL Score 600-650", "Limited Credit History", "Income Verification", "Additional Documents"],
                highlight: false
              }
            ].map((tier, index) => (
              <div key={index} className={`rounded-2xl p-8 ${tier.highlight ? 'bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200' : 'bg-gray-50'}`}>
                <div className="text-center space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">{tier.category}</h3>
                  <div className="text-3xl font-bold" style={{ color: '#340C7F' }}>
                    {tier.rate}
                  </div>
                  <p className="text-sm text-gray-600">Annual Percentage Rate</p>
                  <ul className="space-y-2 text-left">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Frequently Asked <span style={{ color: '#340C7F' }}>Questions</span>
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our personal loan process
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: "What is the maximum loan amount I can get?",
                answer: "The maximum loan amount depends on your monthly income, credit score, and repayment capacity. Generally, we offer loans up to 20 times your monthly salary for salaried individuals and based on ITR for self-employed applicants."
              },
              {
                question: "How long does the approval process take?",
                answer: "Our digital-first approach enables loan approval within 24 hours for most applications. Once approved, the loan amount is disbursed to your bank account within 2-3 working days."
              },
              {
                question: "Can I prepay my personal loan without penalties?",
                answer: "Yes, you can prepay your personal loan after 6 months without any prepayment charges. For loans above ₹1 lakh, there are no prepayment penalties from day one."
              },
              {
                question: "What documents do I need for a personal loan?",
                answer: "You'll need identity proof, address proof, income proof (salary slips/ITR), bank statements, and employment proof. Our team will guide you through the exact requirements based on your profile."
              },
              {
                question: "How is my eligibility determined?",
                answer: "Eligibility is based on age (21-58 years), monthly income (minimum ₹25,000), employment stability, CIBIL score (650+), and debt-to-income ratio. Our team can help assess your eligibility instantly."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Success Stories */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Success <span style={{ color: '#340C7F' }}>Stories</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real stories from satisfied customers who achieved their dreams with our personal loans
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "Priya Sharma",
                city: "Mumbai",
                purpose: "Wedding Expenses",
                amount: "₹8 Lakhs",
                story: "PFIL Finance helped me arrange funds for my dream wedding with minimal documentation and quick approval. The team was supportive throughout the process.",
                rating: 5
              },
              {
                name: "Rajesh Kumar",
                city: "Pune",
                purpose: "Business Expansion",
                amount: "₹12 Lakhs",
                story: "I expanded my restaurant business with their personal loan. The flexible repayment options made it easy to manage my cash flow.",
                rating: 5
              },
              {
                name: "Anita Patel",
                city: "Ahmedabad",
                purpose: "Medical Emergency",
                amount: "₹5 Lakhs",
                story: "During a medical emergency, PFIL Finance provided immediate financial support. The quick approval saved precious time when it mattered most.",
                rating: 5
              }
            ].map((story, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(story.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-lg">★</span>
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{story.story}"</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">{story.name}</p>
                  <p className="text-sm text-gray-600">{story.city} • {story.purpose}</p>
                  <p className="text-sm font-medium" style={{ color: '#340C7F' }}>Loan Amount: {story.amount}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#340C7F' }}>
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Ready to Apply for Personal Loan?
          </h2>
          <p className="text-xl text-blue-100">
            Get fast approval and quick disbursal. Our team will guide you through the entire process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors shadow-lg transform hover:scale-105"
            >
              Apply Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-lg hover:bg-white hover:text-gray-900 transition-colors transform hover:scale-105"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PersonalLoan;